import { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import { useTranslation } from 'react-i18next';
import TeacherShell, { TeacherPageHeader } from '../../../components/layout/TeacherShell';
import { Card, Button, Input } from '../../../components/ui';
import PdfDisplay from '../../../components/stimulus/PdfDisplay';

const CATEGORY_LABELS = {
  novel: 'Novel',
  article: 'Article',
  non_fiction: 'Non-Fiction',
  textbook: 'Textbook',
  reference: 'Reference',
  presentation: 'Presentation',
  poetry: 'Poetry',
  drama: 'Drama',
  other: 'Other',
};

export default function LibraryEdit({ book, categories = [], preview_text, active_courses = [], available_series = [] }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState(book.title || '');
  const [author, setAuthor] = useState(book.author || '');
  const [category, setCategory] = useState(book.category || '');
  const [seriesId, setSeriesId] = useState(book.book_series_id ? String(book.book_series_id) : '');
  const [position, setPosition] = useState(book.position_in_series != null ? String(book.position_in_series) : '');
  const [saving, setSaving] = useState(false);

  const handleDelete = () => {
    if (confirm('Are you sure you want to delete this item? This cannot be undone.')) {
      router.delete(`/teacher/library/${book.id}`);
    }
  };

  const scrollTitle = (
    <>
      <Link href="/teacher/library" className="hover:text-blue-600 dark:hover:text-blue-400">{t('teacherLibrary.title')}</Link>
      {'  ›  '}
      <Link href={`/teacher/library/${book.id}`} className="hover:text-blue-600 dark:hover:text-blue-400">{book.title}</Link>
      {`  ›  ${t('common.edit')}`}
    </>
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setSaving(true);
    router.patch(`/teacher/library/${book.id}`, {
      library_item: {
        title: title.trim(),
        author: author.trim() || null,
        category: category || null,
        book_series_id: seriesId || null,
        position_in_series: (seriesId && position) ? parseInt(position, 10) : null,
      }
    }, {
      onFinish: () => setSaving(false),
    });
  };

  const hasChanges = title.trim() !== (book.title || '') ||
    (author.trim() || '') !== (book.author || '') ||
    (category || '') !== (book.category || '') ||
    (seriesId || '') !== (book.book_series_id ? String(book.book_series_id) : '') ||
    (position || '') !== (book.position_in_series != null ? String(book.position_in_series) : '');

  const hasPdfPreview = book.item_type === 'pdf' && book.file_url;
  const hasTextPreview = !hasPdfPreview && preview_text;
  const hasFilePreview = hasPdfPreview || hasTextPreview;

  return (
    <TeacherShell maxWidth={hasFilePreview ? 'wide' : undefined} scrollTitle={scrollTitle}>
      <TeacherPageHeader
        title={t('teacherLibraryEdit.editItem')}
        subtitle={book.title}
        backLink={`/teacher/library/${book.id}`}
        backLabel={book.title}
      />

      <div className={hasFilePreview ? 'grid grid-cols-12 gap-6' : ''}>
        {/* Form */}
        <form onSubmit={handleSubmit} className={hasFilePreview ? 'col-span-12 lg:col-span-4 space-y-6' : 'max-w-lg space-y-6'}>
          <Card className="p-6 space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('teacherLibraryEdit.titleField')}
              </label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Item title"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('teacherLibraryEdit.author')}
              </label>
              <Input
                value={author}
                onChange={(e) => setAuthor(e.target.value)}
                placeholder={t('teacherLibraryEdit.authorPlaceholder')}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('teacherLibraryEdit.category')}
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/20"
              >
                <option value="">{t('teacherLibraryEdit.noCategory')}</option>
                {categories.map(cat => (
                  <option key={cat} value={cat}>{CATEGORY_LABELS[cat] || cat}</option>
                ))}
              </select>
            </div>

            {available_series.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Series
                </label>
                <select
                  value={seriesId}
                  onChange={(e) => {
                    setSeriesId(e.target.value);
                    if (!e.target.value) setPosition('');
                  }}
                  className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-gray-900/10 dark:focus:ring-white/20"
                >
                  <option value="">No series</option>
                  {available_series.map((s) => (
                    <option key={s.id} value={s.id}>{s.name}</option>
                  ))}
                </select>

                {seriesId && (
                  <div className="mt-2 flex items-center gap-2">
                    <label className="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      Position in series
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={position}
                      onChange={(e) => setPosition(e.target.value)}
                      placeholder="—"
                      className="w-20 px-2 py-1 text-sm rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                )}
              </div>
            )}

            {book.item_type === 'link' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('teacherLibraryEdit.url')}
                </label>
                <a
                  href={book.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 dark:text-blue-400 hover:underline break-all"
                >
                  {book.url}
                </a>
              </div>
            )}

            <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
              <span>{t('teacherLibraryEdit.type')} {(book.item_type || 'epub').toUpperCase()}</span>
              {book.language && <><span>&middot;</span><span>{t('teacherLibraryEdit.language')} {book.language}</span></>}
            </div>
          </Card>

          <div className="flex items-center gap-3">
            <Button type="submit" disabled={saving || !title.trim() || !hasChanges}>
              {saving ? t('common.saving') : t('teacherLibraryEdit.saveChanges')}
            </Button>
            <Link href={`/teacher/library/${book.id}`}>
              <Button type="button" variant="secondary">{t('common.cancel')}</Button>
            </Link>
          </div>

          {/* For non-PDF file types, link to show page to view content */}
          {!hasFilePreview && book.file_url && (
            <p className="text-xs text-gray-400 dark:text-gray-500">
              <Link href={`/teacher/library/${book.id}`} className="hover:text-gray-600 dark:hover:text-gray-300 underline">
                {t('teacherLibraryEdit.viewContent')}
              </Link>
              {' '}{t('teacherLibraryEdit.toReference')}
            </p>
          )}

          {/* Delete */}
          <div className="pt-6 border-t border-gray-200 dark:border-gray-700">
            {active_courses.length > 0 ? (
              <div className="space-y-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('teacherLibraryEdit.cantDelete')} {active_courses.length === 1 ? t('teacherLibraryEdit.activeCourse') : t('teacherLibraryEdit.activeCourses')}:
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {active_courses.map(course => (
                    <Link
                      key={course.id}
                      href={`/teacher/courses/${course.id}`}
                      className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300 hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
                    >
                      {course.title}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <button
                onClick={handleDelete}
                className="text-sm font-medium text-red-500 dark:text-red-400 hover:text-red-600 dark:hover:text-red-300 transition-colors"
              >
                {t('teacherLibraryEdit.deleteItem')}
              </button>
            )}
          </div>
        </form>

        {/* File preview */}
        {hasPdfPreview && (
          <div className="col-span-12 lg:col-span-8">
            <PdfDisplay fileUrl={book.file_url} />
          </div>
        )}
        {hasTextPreview && (
          <div className="col-span-12 lg:col-span-8">
            <Card className="p-6">
              <p className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wide mb-3">Content Preview</p>
              <div className="prose prose-sm dark:prose-invert max-w-none text-gray-600 dark:text-gray-300 leading-relaxed whitespace-pre-wrap">
                {preview_text}
              </div>
            </Card>
          </div>
        )}
      </div>
    </TeacherShell>
  );
}
